(() => {
  if (customElements.get('c2o3-immersive-product')) return;

  const TRANSITION_MODES = new Set([
    'slide',
    'depth-dissolve',
    'atmosphere-lead',
    'soft-wipe',
    'layered-parallax',
  ]);
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smoothstep = (value) => {
    const t = clamp(value);
    return t * t * (3 - 2 * t);
  };
  const parseHexColor = (value) => {
    const hex = String(value || '').trim().replace(/^#/, '');
    if (!/^[\da-f]{3}([\da-f]{3})?$/i.test(hex)) return null;
    const normalized = hex.length === 3 ? [...hex].map((character) => character + character).join('') : hex;
    return [0, 2, 4].map((offset) => parseInt(normalized.slice(offset, offset + 2), 16));
  };
  const mixColor = (from, to, progress) => from.map((channel, index) => Math.round(channel + (to[index] - channel) * progress));
  const relativeLuminance = (color) => {
    const channels = color.map((channel) => {
      const value = channel / 255;
      return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  };
  const contrastRatio = (background, foreground) => {
    const backgroundLuminance = relativeLuminance(background);
    const foregroundLuminance = relativeLuminance(foreground);
    return (Math.max(backgroundLuminance, foregroundLuminance) + 0.05) /
      (Math.min(backgroundLuminance, foregroundLuminance) + 0.05);
  };
  const rgbColor = (color) => `rgb(${color.join(' ')})`;
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class C2o3ImmersiveProduct extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      this.track = this.querySelector('.c2o3-immersive__covers');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._direction = 1;
      this._lastProgress = 0;
      this._onScroll = (event) => this.onGalleryScroll(event);
      this._onSync = () => {
        this.scheduleSync();
        this.scheduleThumbnailSync();
        this.scheduleAtmosphereGeometry();
      };
      this._onMotionChange = () => {
        this.applyTransitionMode();
        this.scheduleSync();
      };
      this._onVariationClick = (event) => this.onVariationClick(event);
      this._onViewportScroll = () => {
        if (this.dataset.backgroundCoverage === 'media-frame') this.scheduleAtmosphereGeometry();
      };

      this.bindMediaRoot(this.querySelector('.c2o3-immersive__media'));
      window.addEventListener('resize', this._onSync);
      window.addEventListener('scroll', this._onViewportScroll, { passive: true });
      this.motionQuery.addEventListener('change', this._onMotionChange);
      this.addEventListener('click', this._onVariationClick);
      this.observeDom();
      this.refreshDom();

      this.hideThumbChevrons();
    }

    disconnectedCallback() {
      this._connected = false;
      this.bindMediaRoot(null);
      window.removeEventListener('resize', this._onSync);
      window.removeEventListener('scroll', this._onViewportScroll);
      this.motionQuery?.removeEventListener('change', this._onMotionChange);
      this.removeEventListener('click', this._onVariationClick);
      this._domObserver?.disconnect();
      this._orderObserver?.disconnect();
      this._geometryObserver?.disconnect();
      this._variationAbort?.abort();
      if (this._raf) cancelAnimationFrame(this._raf);
      if (this._thumbRaf) cancelAnimationFrame(this._thumbRaf);
      if (this._geometryRaf) cancelAnimationFrame(this._geometryRaf);
    }

    bindMediaRoot(nextRoot) {
      if (this.mediaRoot === nextRoot) {
        this.gallery = nextRoot?.querySelector('.c2o3-immersive-gallery');
        return;
      }

      this.mediaRoot?.removeEventListener('scroll', this._onScroll, { capture: true });
      this.mediaRoot?.removeEventListener('slider:change', this._onSync, true);
      this.mediaRoot?.removeEventListener('load', this._onSync, true);
      this.mediaRoot?.removeEventListener('loadedmetadata', this._onSync, true);
      this._orderObserver?.disconnect();
      this._orderObserver = null;
      this.mediaRoot = nextRoot;
      this.gallery = nextRoot?.querySelector('.c2o3-immersive-gallery');

      if (!nextRoot) return;
      nextRoot.addEventListener('scroll', this._onScroll, { capture: true, passive: true });
      nextRoot.addEventListener('slider:change', this._onSync, true);
      nextRoot.addEventListener('load', this._onSync, true);
      nextRoot.addEventListener('loadedmetadata', this._onSync, true);
      this.observeGalleryOrder();
    }

    refreshDom() {
      this.syncKachingProduct();
      this.track = this.querySelector('.c2o3-immersive__covers');
      this.bindMediaRoot(this.querySelector('.c2o3-immersive__media'));
      this.gallery = this.mediaRoot?.querySelector('.c2o3-immersive-gallery');
      this._slots = null;
      this.applyTransitionMode();
      this.matchTrackOrder();
      this.syncTrack();
      this.scheduleThumbnailSync();
      this.observeAtmosphereGeometry();
      this.scheduleAtmosphereGeometry();
      this.hideThumbChevrons();
    }

    syncKachingProduct() {
      const productInfo = this.querySelector('product-info');
      const productId = productInfo?.dataset.productId;
      if (!productId) return;

      productInfo.querySelectorAll('kaching-bundle').forEach((placeholder) => {
        if (placeholder.getAttribute('product-id') === productId) return;

        // Kaching watches inserted placeholders, not product-id attribute changes.
        // A fresh node also discards the previous product's rendered bundle state.
        const nextPlaceholder = placeholder.cloneNode(false);
        nextPlaceholder.setAttribute('product-id', productId);
        nextPlaceholder.removeAttribute('data-initialized');
        placeholder.replaceWith(nextPlaceholder);
      });
    }

    observeDom() {
      this._domObserver?.disconnect();
      this._domObserver = new MutationObserver((mutations) => {
        const galleryChanged = mutations.some((mutation) => {
          if (mutation.type === 'attributes') {
            return mutation.target.matches?.('.c2o3-immersive-gallery');
          }

          const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
          return nodes.some(
            (node) =>
              node.nodeType === 1 &&
              (node.matches?.('.c2o3-immersive__media, .c2o3-immersive-gallery, kaching-bundle') ||
                node.querySelector?.('.c2o3-immersive-gallery, kaching-bundle')),
          );
        });

        if (!galleryChanged || this._refreshQueued) return;
        this._refreshQueued = true;
        queueMicrotask(() => {
          this._refreshQueued = false;
          if (this.isConnected) this.refreshDom();
        });
      });
      this._domObserver.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-transition'],
      });
    }

    get slider() {
      return this.querySelector('.c2o3-immersive__media slider-element');
    }

    get slots() {
      return (this._slots ??= Array.from(this.track?.querySelectorAll('.c2o3-immersive__cover-slot') ?? []));
    }

    get mediaSlides() {
      return Array.from(this.slider?.querySelectorAll('.product__media-list > .product__media') ?? []);
    }

    get requestedMode() {
      const requested = this.gallery?.dataset.transition || 'slide';
      return TRANSITION_MODES.has(requested) ? requested : 'slide';
    }

    get mode() {
      return this.dataset.transition || 'slide';
    }

    applyTransitionMode() {
      this.dataset.transition = prefersReducedMotion() ? 'slide' : this.requestedMode;
    }

    async onVariationClick(event) {
      const link = event.target.closest?.('.c2o3-variation__item');
      if (!link || !this.contains(link)) return;
      if (link.matches('[aria-current="true"]')) {
        event.preventDefault();
        return;
      }
      if (
        event.defaultPrevented ||
        event.button > 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const sectionNode = this.closest('.shopify-section[id^="shopify-section-"]');
      if (!sectionNode) return;

      const productUrl = new URL(link.href, window.location.href);
      if (productUrl.origin !== window.location.origin) return;
      event.preventDefault();

      const sectionId = sectionNode.id.replace(/^shopify-section-/, '');
      const requestUrl = new URL(productUrl.href);
      requestUrl.searchParams.set('section_id', sectionId);
      const viewportTop = sectionNode.getBoundingClientRect().top;
      const currentProductInfo = this.querySelector('product-info');
      const updateUrl = currentProductInfo?.dataset.updateUrl === 'true';

      this._variationAbort?.abort();
      const requestController = new AbortController();
      this._variationAbort = requestController;
      this.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(requestUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: requestController.signal,
        });
        if (!response.ok) throw new Error(`Section request failed (${response.status})`);

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextSection = documentFragment.querySelector(`#${CSS.escape(sectionNode.id)}`);
        if (!nextSection) throw new Error('Rendered section was not returned');

        const nextProductInfo = nextSection.querySelector('product-info');
        if (nextProductInfo) nextProductInfo.dataset.updateUrl = String(updateUrl);

        const restoreVariationFocus = link.contains(document.activeElement);
        sectionNode.replaceWith(nextSection);
        window.scrollBy({ top: nextSection.getBoundingClientRect().top - viewportTop, behavior: 'instant' });
        if (restoreVariationFocus) {
          const nextLink = Array.from(nextSection.querySelectorAll('.c2o3-variation__item')).find(
            (candidate) => new URL(candidate.href).pathname === productUrl.pathname,
          );
          nextLink?.focus({ preventScroll: true });
        }
        if (updateUrl) window.history.replaceState({}, '', productUrl);
        document.dispatchEvent(
          new CustomEvent('c2o3:product:change', {
            detail: { sectionId, productUrl: productUrl.href },
          }),
        );
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[Immersive by StudioLab] Variation update failed.', error);
        window.location.assign(productUrl.href);
      } finally {
        if (this._variationAbort === requestController) this.removeAttribute('aria-busy');
      }
    }

    onGalleryScroll(event) {
      if (event.target?.tagName !== 'SLIDER-ELEMENT') return;
      this.scheduleSync();
    }

    scheduleSync() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = 0;
        this.syncTrack();
      });
    }

    get progress() {
      const slider = this.slider;
      const offset = slider?.itemOffset;
      return offset ? slider.scrollLeft / offset : 0;
    }

    syncTrack() {
      if (!this.track || !this.slider) return;

      const progress = this.progress;
      const delta = progress - this._lastProgress;
      if (Math.abs(delta) > 0.001) this._direction = Math.sign(delta);
      this.syncTextContrast(progress);

      switch (this.mode) {
        case 'depth-dissolve':
          this.renderDepthDissolve(progress);
          break;
        case 'atmosphere-lead':
          this.renderAtmosphereLead(progress);
          break;
        case 'soft-wipe':
          this.renderSoftWipe(progress);
          break;
        case 'layered-parallax':
          this.renderLayeredParallax(progress);
          break;
        default:
          this.renderTraditionalSlide(progress);
      }

      this._lastProgress = progress;
    }

    syncTextContrast(progress) {
      if (this.dataset.autoBuyContrast !== 'true') return;
      const column = this.querySelector('.c2o3-immersive__end .c2o3-immersive-col');
      if (!column || !this.slots.length) return;

      const { from, to, t } = this.transitionState(progress);
      const fromColor = parseHexColor(this.slots[from]?.dataset.contrastColor);
      const toColor = parseHexColor(this.slots[to]?.dataset.contrastColor) || fromColor;
      if (!fromColor || !toColor) return;

      const light = parseHexColor(this.dataset.contrastLight);
      const dark = parseHexColor(this.dataset.contrastDark);
      if (!light || !dark) return;

      const background = mixColor(fromColor, toColor, smoothstep(t));
      const useDark = contrastRatio(background, dark) > contrastRatio(background, light);
      const tone = useDark ? 'dark' : 'light';
      if (column.dataset.contrastTone === tone) return;

      const textColor = useDark ? dark : light;
      column.dataset.contrastTone = tone;
      column.style.setProperty('--c2o3-auto-text-color', rgbColor(textColor));
      column.style.setProperty('--color-on-ink', rgbColor(textColor));
      column.style.setProperty('--color-foreground', textColor.join(' '));
    }

    transitionState(progress) {
      const lastIndex = Math.max(0, this.mediaSlides.length - 1);
      const value = clamp(progress, 0, lastIndex);

      if (this._direction < 0) {
        const from = Math.min(lastIndex, Math.ceil(value));
        const to = Math.max(0, from - 1);
        return { from, to, t: from === to ? 0 : clamp(from - value), direction: -1 };
      }

      const from = Math.min(lastIndex, Math.floor(value));
      const to = Math.min(lastIndex, from + 1);
      return { from, to, t: from === to ? 0 : clamp(value - from), direction: 1 };
    }

    resetCoverState() {
      this.slots.forEach((slot) => {
        slot.style.setProperty('--c2o3-cover-opacity', '0');
        slot.style.setProperty('--c2o3-cover-scale', '1');
        slot.style.setProperty('--c2o3-cover-z', '0');
        slot.style.setProperty('--c2o3-wipe-mask', 'none');
      });
    }

    resetMediaState(progress, stacked = false) {
      this.mediaSlides.forEach((slide, index) => {
        slide.style.setProperty('--c2o3-media-opacity', stacked ? '0' : '1');
        slide.style.setProperty('--c2o3-media-scale', '1');
        slide.style.setProperty('--c2o3-media-stack', stacked ? `${(progress - index) * 100}%` : '0%');
        slide.style.setProperty('--c2o3-media-entry', '0%');
        slide.style.setProperty('--c2o3-media-z', '0');
      });
    }

    setCover(index, { opacity = 1, scale = 1, z = 1, mask = 'none' } = {}) {
      const slot = this.slots[index];
      if (!slot) return;
      slot.style.setProperty('--c2o3-cover-opacity', String(opacity));
      slot.style.setProperty('--c2o3-cover-scale', String(scale));
      slot.style.setProperty('--c2o3-cover-z', String(z));
      slot.style.setProperty('--c2o3-wipe-mask', mask);
    }

    setMedia(index, { opacity = 1, scale = 1, entry = 0, z = 1 } = {}) {
      const slide = this.mediaSlides[index];
      if (!slide) return;
      slide.style.setProperty('--c2o3-media-opacity', String(opacity));
      slide.style.setProperty('--c2o3-media-scale', String(scale));
      slide.style.setProperty('--c2o3-media-entry', `${entry}%`);
      slide.style.setProperty('--c2o3-media-z', String(z));
    }

    renderMobileCovers(progress) {
      const { from, to, t } = this.transitionState(progress);
      this.resetCoverState();

      if (from === to || prefersReducedMotion()) {
        this.setCover(prefersReducedMotion() ? Math.round(clamp(progress, 0, this.slots.length - 1)) : from);
        return;
      }

      const eased = smoothstep(t);
      this.setCover(from, { opacity: 1 - eased, z: 1 });
      this.setCover(to, { opacity: eased, z: 2 });
    }

    renderTraditionalSlide(progress) {
      if (window.matchMedia('(max-width: 1023px)').matches) {
        this.track.style.transform = '';
        this.track.style.setProperty('--p', progress);
        this.renderMobileCovers(progress);
        this.resetMediaState(progress);
        return;
      }

      this.track.style.transform = `translate3d(${-progress * 100}%, 0, 0)`;
      this.track.style.setProperty('--p', progress);
      this.resetMediaState(progress);
    }

    renderDepthDissolve(progress) {
      const { from, to, t } = this.transitionState(progress);
      const eased = smoothstep(t);
      this.track.style.transform = '';
      this.resetCoverState();
      this.resetMediaState(progress, true);

      if (from === to) {
        this.setCover(from);
        this.setMedia(from);
        return;
      }

      this.setCover(from, { opacity: 1 - eased, scale: 1 + 0.03 * eased, z: 1 });
      this.setCover(to, { opacity: eased, scale: 1.055 - 0.055 * eased, z: 2 });
      this.setMedia(from, { opacity: 1 - eased, scale: 1 + 0.018 * eased, z: 1 });
      this.setMedia(to, { opacity: eased, scale: 0.975 + 0.025 * eased, z: 2 });
    }

    renderAtmosphereLead(progress) {
      const { from, to, t } = this.transitionState(progress);
      const lead = 1 - (1 - t) ** 2;
      const backgroundProgress = from + (to - from) * lead;
      const isMobile = window.matchMedia('(max-width: 1023px)').matches;
      this.track.style.transform = isMobile ? '' : `translate3d(${-backgroundProgress * 100}%, 0, 0)`;
      this.track.style.setProperty('--p', backgroundProgress);
      if (isMobile) this.renderMobileCovers(backgroundProgress);
      this.resetMediaState(progress);
    }

    renderSoftWipe(progress) {
      const { from, to, t, direction } = this.transitionState(progress);
      const eased = smoothstep(t);
      this.track.style.transform = '';
      this.resetCoverState();
      this.resetMediaState(progress, true);

      if (from === to) {
        this.setCover(from);
        this.setMedia(from);
        return;
      }

      const edge = (1 - eased) * 100;
      const featherStart = clamp(edge - 10, 0, 100);
      const featherEnd = clamp(edge + 10, 0, 100);
      const wipeDirection = direction > 0 ? 'to right' : 'to left';
      const mask =
        eased > 0.995
          ? 'none'
          : `linear-gradient(${wipeDirection}, transparent 0%, transparent ${featherStart}%, #000 ${featherEnd}%, #000 100%)`;

      this.setCover(from, { z: 1 });
      this.setCover(to, { z: 2, mask });
      this.setMedia(from, { opacity: 1 - eased, z: 1 });
      this.setMedia(to, {
        opacity: eased,
        entry: (to - from) * 8 * (1 - eased),
        z: 2,
      });
    }

    renderLayeredParallax(progress) {
      const isMobile = window.matchMedia('(max-width: 1023px)').matches;
      this.track.style.transform = isMobile ? '' : `translate3d(${-progress * 100}%, 0, 0)`;
      this.track.style.setProperty('--p', progress);
      if (isMobile) this.renderMobileCovers(progress);
      this.resetMediaState(progress);
    }

    scheduleThumbnailSync() {
      if (this._thumbRaf) return;
      this._thumbRaf = requestAnimationFrame(() => {
        this._thumbRaf = 0;
        this.syncActiveThumbnail();
      });
    }

    observeAtmosphereGeometry() {
      this._geometryObserver?.disconnect();
      this._geometryObserver = null;
      if (!('ResizeObserver' in window) || !this.mediaRoot) return;
      const frame = this.slider;
      if (!frame) return;
      this._geometryObserver = new ResizeObserver(() => this.scheduleAtmosphereGeometry());
      this._geometryObserver.observe(this.mediaRoot);
      this._geometryObserver.observe(frame);
    }

    scheduleAtmosphereGeometry() {
      if (this._geometryRaf) return;
      this._geometryRaf = requestAnimationFrame(() => {
        this._geometryRaf = 0;
        this.syncAtmosphereGeometry();
      });
    }

    syncAtmosphereGeometry() {
      this.syncThumbnailWidth();
      this.syncMediaDiffusionGeometry();
      const atmosphere = this.mediaRoot?.querySelector('.c2o3-immersive__atmosphere');
      if (!atmosphere) return;
      if (this.dataset.backgroundCoverage !== 'media-frame') {
        ['top', 'left', 'width', 'height'].forEach((key) =>
          atmosphere.style.removeProperty(`--c2o3-atmosphere-${key}`),
        );
        return;
      }

      const frame = this.slider;
      if (!frame) return;
      const rootRect = this.mediaRoot.getBoundingClientRect();
      const frameRect = frame.getBoundingClientRect();
      atmosphere.style.setProperty('--c2o3-atmosphere-top', `${frameRect.top - rootRect.top}px`);
      atmosphere.style.setProperty('--c2o3-atmosphere-left', `${frameRect.left - rootRect.left}px`);
      atmosphere.style.setProperty('--c2o3-atmosphere-width', `${frameRect.width}px`);
      atmosphere.style.setProperty('--c2o3-atmosphere-height', `${frameRect.height}px`);
    }

    syncMediaDiffusionGeometry() {
      if (!this.hasAttribute('data-c2o3-featured-immersive')) return;
      this.gallery?.querySelectorAll('.product__media-list .product__media :is(img, video)').forEach((media) => {
        const width = media.videoWidth || media.naturalWidth || Number(media.getAttribute('width'));
        const height = media.videoHeight || media.naturalHeight || Number(media.getAttribute('height'));
        const styles = getComputedStyle(media);
        let size = '100% 100%';
        if (styles.objectFit === 'contain' && width > 0 && height > 0 && media.clientWidth > 0 && media.clientHeight > 0) {
          const scale = Math.min(media.clientWidth / width, media.clientHeight / height);
          size = `${width * scale}px ${height * scale}px`;
        }
        // Preserve the native frame, image proportions, zoom and slideshow motion.
        // Only the mask follows the visible object inside a contain frame.
        if (media.style.getPropertyValue('--c2o3-media-mask-size') !== size) {
          media.style.setProperty('--c2o3-media-mask-size', size);
        }
        if (media.style.getPropertyValue('--c2o3-media-mask-position') !== styles.objectPosition) {
          media.style.setProperty('--c2o3-media-mask-position', styles.objectPosition);
        }
      });
    }

    syncThumbnailWidth() {
      const strip = this.mediaRoot?.querySelector('.c2o3-compact-thumbnails');
      if (!strip) return;
      const property = '--c2o3-visible-media-width';
      if (!window.matchMedia('(min-width: 1024px)').matches) {
        strip.style.removeProperty(property);
        return;
      }

      const slides = this.slider?.itemsToShow || this.mediaSlides;
      const slide = slides[Math.round(this.progress)] || slides[0];
      const media = slide?.querySelector('video, img');
      const width = media?.videoWidth || media?.naturalWidth || Number(media?.getAttribute('width'));
      const height = media?.videoHeight || media?.naturalHeight || Number(media?.getAttribute('height'));
      if (!media || !width || !height) {
        strip.style.removeProperty(property);
        return;
      }

      const visibleWidth = getComputedStyle(media).objectFit === 'contain'
        ? Math.min(media.clientWidth, media.clientHeight * width / height)
        : media.clientWidth;
      if (visibleWidth > 0) strip.style.setProperty(property, `${visibleWidth}px`);
    }

    syncActiveThumbnail() {
      const strip = this.mediaRoot?.querySelector('.product__thumbnails');
      const active = strip?.querySelector('[aria-current="true"]');
      if (!strip || !active) return;

      const stripBounds = strip.getBoundingClientRect();
      const activeBounds = active.getBoundingClientRect();
      if (activeBounds.left >= stripBounds.left && activeBounds.right <= stripBounds.right) return;

      const left = active.offsetLeft - (strip.clientWidth - active.offsetWidth) / 2;
      strip.scrollTo({
        left: Math.max(0, left),
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }

    // The gallery re-renders server-side on variant:change (new featured media first,
    // slider resets to 0). Mirror that order in the cover track or the atmosphere
    // falls out of sync with the photos.
    observeGalleryOrder() {
      const list = this.mediaRoot.querySelector('.product__media-list');
      if (!list) return;
      this._orderObserver = new MutationObserver(() => {
        this.matchTrackOrder();
        this.scheduleSync();
      });
      this._orderObserver.observe(list, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-media-id'],
      });
    }

    matchTrackOrder() {
      const list = this.mediaRoot?.querySelector('.product__media-list');
      if (!list || !this.track) return;
      const slotsById = new Map(this.slots.map((slot) => [slot.dataset.mediaId, slot]));
      list.querySelectorAll(':scope > [data-media-id]').forEach((slide) => {
        const slot = slotsById.get(slide.dataset.mediaId);
        if (slot) this.track.appendChild(slot);
      });
      this._slots = null;
    }

    hideThumbChevrons() {
      const paint = (host) => {
        const root = host.shadowRoot;
        if (!root || root.querySelector('[data-c2o3-arrow]')) return Boolean(root);
        const style = document.createElement('style');
        style.setAttribute('data-c2o3-arrow', '');
        style.textContent = 's{display:none}';
        root.appendChild(style);
        return true;
      };

      this.querySelectorAll('scroll-shadow').forEach((host) => {
        if (paint(host)) return;
        requestAnimationFrame(() => paint(host));
      });
    }
  }

  customElements.define('c2o3-immersive-product', C2o3ImmersiveProduct);
})();
